/**
 * F42 (Wave 2 — B2) — bulk venue action panel (suspend / restore / flag).
 *
 * Shows the selected venues, an action picker + mandatory reason, a confirm
 * dialog previewing the affected list, then submits via
 * `adminVenuesMergeApi.bulk` with a client-generated Idempotency-Key. Renders
 * the per-venue partial-success result ({ venueId, ok, error }[]).
 *
 * Rendered inside B1's venue-table page (Drawer-mounted). Design-flagged for a
 * later polish pass (the partial-success result panel).
 */
import { useEffect, useMemo, useState } from 'react';
import { useI18n } from '@bravemove/i18n/react';
import {
  Badge,
  Banner,
  Button,
  Dialog,
  Drawer,
  FormField,
  Select,
  Textarea,
} from '@bravemove/ui-web';
import {
  BULK_VENUE_CAP,
  FLAG_REASON_MIN,
  type BulkActionResult,
  type BulkVenueAction,
} from '@bravemove/types';
import { adminVenuesMergeApi } from '../../../api/adminVenuesMerge.js';

export interface BulkPanelVenue {
  readonly id: string;
  readonly name: string;
}

export interface BulkPanelProps {
  readonly open: boolean;
  readonly selected: readonly BulkPanelVenue[];
  readonly onClose: () => void;
  /** Called once the bulk action completes (B1 refetches the list). */
  readonly onDone: (result: BulkActionResult) => void;
  /** Action preselected when the panel opens (the toolbar entry button). */
  readonly initialAction?: BulkVenueAction;
}

/** Test-friendly UUID generator — falls back when `crypto.randomUUID` is missing. */
function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function BulkPanel({
  open,
  selected,
  onClose,
  onDone,
  initialAction = 'suspend',
}: BulkPanelProps) {
  const { t } = useI18n();
  const [action, setAction] = useState<BulkVenueAction>(initialAction);
  const [reason, setReason] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<BulkActionResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /* eslint-disable react-hooks/set-state-in-effect -- reset transient state when re-opened */
  useEffect(() => {
    if (!open) return;
    setAction(initialAction);
    setReason('');
    setConfirmOpen(false);
    setSubmitting(false);
    setResult(null);
    setError(null);
  }, [open, initialAction]);
  /* eslint-enable react-hooks/set-state-in-effect */

  const trimmed = reason.trim();
  const overCap = selected.length > BULK_VENUE_CAP;
  const reasonValid = trimmed.length >= FLAG_REASON_MIN;
  const canSubmit = reasonValid && !overCap && selected.length > 0;

  const nameById = useMemo(() => new Map(selected.map((v) => [v.id, v.name] as const)), [selected]);

  const actionOptions = useMemo(
    () => [
      {
        value: 'suspend',
        label: t('admin:venueMerge.bulk.action.suspend', { defaultValue: 'Suspend' }),
      },
      {
        value: 'restore',
        label: t('admin:venueMerge.bulk.action.restore', { defaultValue: 'Restore' }),
      },
      {
        value: 'flag',
        label: t('admin:venueMerge.bulk.action.flag', { defaultValue: 'Flag for review' }),
      },
    ],
    [t],
  );

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await adminVenuesMergeApi.bulk(
        { action, venueIds: selected.map((v) => v.id), reason: trimmed },
        newIdempotencyKey(),
      );
      setResult(res);
      setConfirmOpen(false);
      onDone(res);
    } catch {
      setError(
        t('admin:venueMerge.bulk.error', {
          defaultValue: 'The bulk action could not be submitted.',
        }),
      );
      setConfirmOpen(false);
    } finally {
      setSubmitting(false);
    }
  };

  const okCount = result?.results.filter((r) => r.ok).length ?? 0;
  const failedCount = result?.results.filter((r) => !r.ok).length ?? 0;

  return (
    <>
      <Drawer
        open={open}
        onClose={onClose}
        title={t('admin:venueMerge.bulk.panelTitle', { defaultValue: 'Bulk action' })}
        width={460}
        footer={
          result ? (
            <Button variant="primary" onClick={onClose} data-testid="bulk-panel-close">
              {t('admin:venueMerge.bulk.close', { defaultValue: 'Close' })}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={onClose} disabled={submitting}>
                {t('admin:venueMerge.bulk.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button
                variant="primary"
                onClick={() => setConfirmOpen(true)}
                disabled={!canSubmit || submitting}
                data-testid="bulk-panel-submit"
              >
                {t('admin:venueMerge.bulk.submit', { defaultValue: 'Run bulk action' })}
              </Button>
            </>
          )
        }
      >
        <div className="flex flex-col gap-4" data-testid="bulk-panel">
          <p className="text-[13px] font-medium text-ink">
            {t('admin:venueMerge.bulk.selectedCount', {
              defaultValue: '{count, plural, one {# venue selected} other {# venues selected}}',
              count: selected.length,
            })}
          </p>

          {overCap ? (
            <Banner tone="danger">
              {t('admin:venueMerge.bulk.capExceeded', {
                defaultValue: 'You can act on at most 100 venues at once.',
              })}
            </Banner>
          ) : null}

          {error ? <Banner tone="danger">{error}</Banner> : null}

          {result ? (
            <div data-testid="bulk-panel-result" className="flex flex-col gap-2">
              <Banner tone={failedCount > 0 ? 'warning' : 'success'}>
                {t('admin:venueMerge.bulk.resultSummary', {
                  defaultValue: '{ok} succeeded, {failed} failed.',
                  ok: okCount,
                  failed: failedCount,
                })}
              </Banner>
              <ul className="flex flex-col gap-1">
                {result.results.map((r) => (
                  <li
                    key={r.venueId}
                    className="flex items-center justify-between gap-3 rounded-md border border-hairline-2 px-3 py-2"
                  >
                    <span className="text-[12.5px] text-ink truncate">
                      {nameById.get(r.venueId) ?? r.venueId}
                    </span>
                    {r.ok ? (
                      <Badge tone="success" size="sm">
                        {t('admin:venueMerge.bulk.resultOk', { defaultValue: 'Done' })}
                      </Badge>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Badge tone="danger" size="sm">
                          {t('admin:venueMerge.bulk.resultFailed', { defaultValue: 'Failed' })}
                        </Badge>
                        {r.error ? (
                          <span className="font-mono text-[11px] text-ink-3">{r.error}</span>
                        ) : null}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <>
              <FormField label={t('admin:venueMerge.bulk.actionLabel', { defaultValue: 'Action' })}>
                <Select
                  value={action}
                  options={actionOptions}
                  onChange={(e) => setAction(e.target.value as BulkVenueAction)}
                  data-testid="bulk-panel-action"
                />
              </FormField>

              <FormField
                label={t('admin:venueMerge.bulk.reasonLabel', { defaultValue: 'Reason' })}
                helper={t('admin:venueMerge.bulk.reasonHelper', {
                  defaultValue:
                    'Required — applied to every selected venue and recorded in the audit log.',
                })}
                required
              >
                <Textarea
                  rows={3}
                  maxLength={2000}
                  showCounter
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  data-testid="bulk-panel-reason"
                />
              </FormField>

              <div>
                <p className="text-[11px] font-semibold uppercase tracking-wide text-ink-3 mb-1">
                  {t('admin:venueMerge.bulk.affectedHeading', { defaultValue: 'Affected venues' })}
                </p>
                <ul className="flex flex-col gap-1">
                  {selected.map((v) => (
                    <li
                      key={v.id}
                      className="text-[12.5px] text-ink-2 rounded-md bg-surface-2 px-3 py-1.5 truncate"
                    >
                      {v.name}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      </Drawer>

      <Dialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => void submit()}
        destructive={action === 'suspend'}
        loading={submitting}
        title={t('admin:venueMerge.bulk.confirmTitle', {
          defaultValue: 'Apply to {count} venues?',
          count: selected.length,
        })}
        description={t('admin:venueMerge.bulk.confirmBody', {
          defaultValue:
            'This action is applied to each selected venue. Some may fail individually — you will see a per-venue result.',
        })}
        confirmLabel={t('admin:venueMerge.bulk.confirmCta', { defaultValue: 'Apply' })}
        cancelLabel={t('admin:venueMerge.bulk.cancel', { defaultValue: 'Cancel' })}
      >
        <ul className="flex flex-col gap-1" data-testid="bulk-panel-confirm-list">
          {selected.map((v) => (
            <li key={v.id} className="text-[12px] text-ink-2 truncate">
              {v.name}
            </li>
          ))}
        </ul>
      </Dialog>
    </>
  );
}
